import React from "react";
import logo from "../../../assets/svg/auth/logo.svg";
import useCustomScrollbar from "../../../styles/useCustomScrollbar";
import "../../../styles/customScrollbar.css";

interface AuthLayoutProps {
  children: React.ReactNode;
  /** A wider column, for a screen whose rows do not read well at form width. */
  wide?: boolean;
}

/**
 * Shared shell for the sign-in screens: logo pinned top-left, the form column
 * centred in the viewport.
 *
 * The same shell the dietitian app's auth screens use, and deliberately so —
 * an admin signing in should be looking at Naurist, not at a different product
 * that happens to share a logo. No card: the column is the form, and a border
 * drawn round it on an otherwise empty white page is a box for the sake of a
 * box.
 *
 * The screen scrolls inside itself rather than letting the document scroll,
 * which is what puts the app's own overlay scrollbar on it instead of the
 * platform's — the same one the signed-in shell uses. `overflow: auto` comes
 * with the class, so it still only appears when a screen is actually too tall.
 */
const AuthLayout: React.FC<AuthLayoutProps> = ({ children, wide = false }) => {
  // Reveals the thumb on hover, scroll and drag. Safe to call per auth screen:
  // it attaches once per element and cleans up on unmount.
  useCustomScrollbar();

  return (
    <div className="h-screen bg-white custom-scrollbar-overlay">
      {/* `min-h-full`, not `min-h-screen`: the height to fill is the scroll
          container's, or a screen taller than the viewport would be measured
          against the viewport twice and leave a dead band below the form. */}
      <div className="min-h-full flex flex-col px-5 py-6 sm:px-12 sm:py-10 3xl:px-16 3xl:py-14">
        <header>
          <img src={logo} alt="Naurist" className="h-6 3xl:h-7 w-auto" />
        </header>

        <main className="flex-1 flex items-center justify-center py-10">
          <div
            className={`w-full ${
              wide
                ? "max-w-[560px] 3xl:max-w-[620px]"
                : "max-w-[460px] 3xl:max-w-[520px]"
            }`}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AuthLayout;
